import { useEffect, useState } from "react";
import { defaultAxiosInstance } from "../api/defaultInstance";
import "../css/AdManagement.css";
import Layout from "./Layout";

function TrashCampaignManagement() {
  const [isLoading, setIsLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [adProductMetas, setAdProductMetas] = useState([]);
  const [filter, setFilter] = useState({
    name: "",
    createdAt: "",
    adProductMetaId: "",
    page: 1,
    limit: 50,
  });
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // 상품 목록 조회
  const fetchAdProductMetas = async () => {
    try {
      const response = await defaultAxiosInstance.get("/advertisement/admin/products/meta/simple");
      if (response.data.status === 200) {
        setAdProductMetas(response.data.data || []);
      }
    } catch (error) {
      console.error("메타 상품 목록 조회 오류:", error);
    }
  };

  // 휴지통 캠페인 조회
  const fetchTrashCampaigns = async (currentFilter = filter) => {
    setIsLoading(true);
    try {
      const params = {
        page: currentFilter.page,
        limit: currentFilter.limit,
      };

      if (currentFilter.name) {
        params.name = currentFilter.name;
      }
      if (currentFilter.createdAt) {
        params.createdAt = currentFilter.createdAt;
      }
      if (currentFilter.adProductMetaId) {
        params.adProductMetaId = currentFilter.adProductMetaId;
      }

      const response = await defaultAxiosInstance.get("/advertisement/admin/campaigns/trash", {
        params,
      });

      if (response.data.status === 200) {
        const result = response.data.data;
        setCampaigns(result.campaigns || []);
        setTotalCount(result.totalCount || 0);
      }
    } catch (error) {
      console.error("휴지통 캠페인 조회 오류:", error);
      alert("휴지통 캠페인 조회 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 초기 로딩
  useEffect(() => {
    fetchAdProductMetas();
    fetchTrashCampaigns();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter((prev) => ({ ...prev, [name]: value }));
  };

  // 필터 적용
  const applyFilter = () => {
    const newFilter = { ...filter, page: 1 };
    setFilter(newFilter);
    setSelectedCampaigns([]);
    fetchTrashCampaigns(newFilter);
  };

  const resetFilter = () => {
    const initialFilter = {
      name: "",
      createdAt: "",
      adProductMetaId: "",
      page: 1,
      limit: 50,
    };
    setFilter(initialFilter);
    setSelectedCampaigns([]);
    fetchTrashCampaigns(initialFilter);
  };

  const handleCheckboxChange = (adCampaignMetaId) => {
    setSelectedCampaigns((prev) =>
      prev.includes(adCampaignMetaId) ? prev.filter((id) => id !== adCampaignMetaId) : [...prev, adCampaignMetaId],
    );
  };

  // 헤더 체크박스: 전체 선택/해제
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedCampaigns(campaigns.map((campaign) => campaign.adCampaignMetaId));
    } else {
      setSelectedCampaigns([]);
    }
  };

  // 복구 버튼 클릭
  const handleRestoreClick = async () => {
    if (selectedCampaigns.length === 0) {
      alert("복구할 캠페인을 선택해주세요");
      return;
    }

    const confirmMessage = `선택한 ${selectedCampaigns.length}개의 캠페인을 복구하시겠습니까?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setIsLoading(true);
      const response = await defaultAxiosInstance.post("/advertisement/admin/campaigns/restore", {
        adCampaignMetaIds: selectedCampaigns,
      });

      if (response.data.status === 200) {
        alert("캠페인이 성공적으로 복구되었습니다.");
        setSelectedCampaigns([]);
        fetchTrashCampaigns();
      }
    } catch (error) {
      console.error("캠페인 복구 오류:", error);
      alert("캠페인 복구 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  // 페이징: 이전 버튼
  const handlePrevPage = () => {
    const newPage = Math.max(1, filter.page - 1);
    const newFilter = { ...filter, page: newPage };
    setFilter(newFilter);
    fetchTrashCampaigns(newFilter);
  };

  // 페이징: 다음 버튼
  const handleNextPage = () => {
    const newPage = filter.page + 1;
    const newFilter = { ...filter, page: newPage };
    setFilter(newFilter);
    fetchTrashCampaigns(newFilter);
  };

  const handlePageClick = (page) => {
    const newFilter = { ...filter, page };
    setFilter(newFilter);
    fetchTrashCampaigns(newFilter);
  };

  // 시작 인덱스와 끝 인덱스 계산
  const startIndex = (filter.page - 1) * filter.limit + 1;
  const endIndex = Math.min(filter.page * filter.limit, totalCount);

  return (
    <Layout>
      <div className="ad-management-container">
        <div className="filter-bar">
          <div className="filter-group">
            <label>이름</label>
            <input
              type="text"
              name="name"
              placeholder="구매자명"
              onChange={handleFilterChange}
              value={filter.name}
              onKeyDown={(e) => {
                if (e.key === "Enter") applyFilter();
              }}
            />
          </div>
          <div className="filter-group">
            <label>접수시간</label>
            <input type="date" name="createdAt" onChange={handleFilterChange} value={filter.createdAt} />
          </div>
          <div className="filter-group">
            <label>캠페인 타입</label>
            <select name="adProductMetaId" onChange={handleFilterChange} value={filter.adProductMetaId}>
              <option value="">전체</option>
              {adProductMetas.map((adProductMeta) => (
                <option key={adProductMeta.adProductMetaId} value={adProductMeta.adProductMetaId}>
                  {adProductMeta.productMetaName}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <button onClick={applyFilter}>필터 적용</button>
            <button onClick={resetFilter} className="reset-btn">
              초기화
            </button>
          </div>
        </div>

        <div className="table-header">
          <h3 className="section-title">휴지통 캠페인 목록</h3>
          <div
            className="button-group"
            style={{ pointerEvents: isLoading ? "none" : "auto", opacity: isLoading ? 0.5 : 1 }}
          >
            <button
              className="status-btn restore"
              onClick={handleRestoreClick}
              disabled={isLoading || selectedCampaigns.length === 0}
            >
              복구
            </button>
          </div>
        </div>

        <div className="ad-table">
          <table>
            <thead>
              <tr>
                <th className="center-cell">
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={campaigns.length > 0 && selectedCampaigns.length === campaigns.length}
                  />
                </th>
                <th className="center-cell">캠페인 ID</th>
                <th className="center-cell">캐랭접수시간</th>
                <th className="center-cell">구매자명(ID)</th>
                <th className="center-cell">플레이스명</th>
                <th className="center-cell">캠페인 타입</th>
                <th className="center-cell">결제수단</th>
                <th className="center-cell">총액</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.length === 0 ? (
                <tr>
                  <td colSpan="8" className="center-cell">
                    {isLoading ? "로딩 중..." : "조회된 캠페인이 없습니다."}
                  </td>
                </tr>
              ) : (
                campaigns.map((campaign) => (
                  <tr key={campaign.adCampaignMetaId} className="center-cell">
                    <td className="center-cell">
                      <input
                        type="checkbox"
                        checked={selectedCampaigns.includes(campaign.adCampaignMetaId)}
                        onChange={() => handleCheckboxChange(campaign.adCampaignMetaId)}
                      />
                    </td>
                    <td className="center-cell">{campaign.adCampaignMetaId}</td>
                    <td className="center-cell">{campaign.createdAt}</td>
                    <td className="center-cell">
                      {campaign.name}
                      {campaign.userId ? ` (${campaign.userId})` : ""}
                    </td>
                    <td className="center-cell">{campaign.placeName}</td>
                    <td className="center-cell">{campaign.adName}</td>
                    <td className="center-cell">
                      {campaign.paymentType === "v_bank"
                        ? "가상계좌"
                        : campaign.paymentType === "card"
                          ? "카드"
                          : campaign.paymentType === "account"
                            ? "계좌이체"
                            : campaign.paymentType || "-"}
                    </td>
                    <td className="center-cell">{campaign.totalCost?.toLocaleString()}원</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination-info">
          <div>
            총 {totalCount}개 항목 중 {startIndex} - {endIndex}
          </div>
          <div className="pagination">
            <button onClick={handlePrevPage} disabled={filter.page === 1}>
              이전
            </button>
            {Array.from({ length: Math.ceil(totalCount / filter.limit) }, (_, i) => (
              <button
                key={i + 1}
                className={filter.page === i + 1 ? "active" : ""}
                onClick={() => handlePageClick(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button onClick={handleNextPage} disabled={endIndex >= totalCount}>
              다음
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default TrashCampaignManagement;
